import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { X, Send, Paperclip, Loader2, Headset } from 'lucide-react';
import { supportAPI } from '../../services/api';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';

const SupportModal = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation();
    const { user, getUserType } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        title: '',
        message: '',
        attachments: null
    });

    const userType = getUserType();
    const modalDirection = typeof i18n?.dir === 'function' ? i18n.dir() : 'ltr';
    const isRTL = modalDirection === 'rtl';

    useEffect(() => {
        if (user && isOpen) {
            setFormData(prev => ({
                ...prev,
                name: user.name || (user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : ''),
                email: user.email || ''
            }));
        }
    }, [user, isOpen]);

    if (!isOpen) return null;

    // Color schemes based on user type
    const getColorScheme = (type) => {
        switch (type) {
            case "PET_GUARDIAN": return { primary: "#8548A8", hover: "#7A4397" };
            case "VETERINARIAN": return { primary: "#00D4B3", hover: "#00C4A7" };
            case "SERVICE_PROVIDER": return { primary: "#D65745", hover: "#C7493A" };
            default: return { primary: "#8548A8", hover: "#7A4397" };
        }
    };

    const colors = getColorScheme(userType);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await supportAPI.submitTicket(formData);
            showSuccessToast(t('modals.support.success'));
            // Clear form for next time (except name/email)
            setFormData(prev => ({
                ...prev,
                title: '',
                message: '',
                attachments: null
            }));
            onClose();
        } catch (error) {
            console.error('Error submitting support ticket:', error);
            showErrorToast(t('common.error') || 'Failed to send feedback message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData({ ...formData, attachments: e.target.files[0] });
        }
    };

    return (
        <div
            style={styles.overlay}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSubmitting) {
                    onClose();
                }
            }}
        >
            <div
                style={{
                    ...styles.modal,
                    direction: modalDirection,
                    transform: isOpen ? 'scale(1)' : 'scale(0.95)',
                    opacity: isOpen ? 1 : 0
                }}
            >
                <div style={styles.header}>
                    <div style={styles.titleGroup}>
                        <div style={{ ...styles.iconWrapper, backgroundColor: `${colors.primary}15` }}>
                            <Headset size={24} color={colors.primary} />
                        </div>
                        <h2 style={styles.title}>{t('modals.support.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        style={styles.closeBtn}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={20} color="#6B7280" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.row}>
                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                            <label style={styles.label}>{t('modals.support.nameLabel')}</label>
                            <input
                                type="text"
                                value={formData.name}
                                readOnly
                                style={styles.inputReadOnly}
                            />
                        </div>

                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                            <label style={styles.label}>{t('modals.support.emailLabel')}</label>
                            <input
                                type="email"
                                value={formData.email}
                                readOnly
                                style={styles.inputReadOnly}
                            />
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('modals.support.ticketTitleLabel')}</label>
                        <input
                            type="text"
                            placeholder={t('modals.support.placeholderTitle')}
                            required
                            disabled={isSubmitting}
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('modals.support.messageLabel')}</label>
                        <textarea
                            rows="4"
                            placeholder={t('modals.support.placeholderMessage')}
                            required
                            disabled={isSubmitting}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            style={styles.textarea}
                        />
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('modals.support.attachmentsLabel')}</label>
                        <div
                            style={{
                                ...styles.fileInputWrapper,
                                borderColor: formData.attachments ? colors.primary : '#D1D5DB'
                            }}
                        >
                            <Paperclip size={18} color={formData.attachments ? colors.primary : "#9CA3AF"} />
                            <input
                                type="file"
                                onChange={handleFileChange}
                                disabled={isSubmitting}
                                style={styles.fileInput}
                            />
                            <span style={{
                                ...styles.fileLabel,
                                color: formData.attachments ? '#111827' : '#6B7280'
                            }}>
                                {formData.attachments ? formData.attachments.name : t('common.chooseFile')}
                            </span>
                            {formData.attachments && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setFormData({ ...formData, attachments: null });
                                    }}
                                    style={styles.clearFileBtn}
                                >
                                    <X size={14} color="#6B7280" />
                                </button>
                            )}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                            ...styles.submitBtn,
                            backgroundColor: colors.primary,
                            opacity: isSubmitting ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = colors.hover;
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = `0 4px 12px ${colors.primary}40`;
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = colors.primary;
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 size={18} style={styles.spinner} />
                                {t('modals.support.submitting')}
                            </>
                        ) : (
                            <>
                                <Send size={18} />
                                {t('modals.support.submit')}
                            </>
                        )}
                    </button>
                </form>
            </div>
            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 100001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)',
        transition: 'all 0.3s ease'
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '560px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    header: {
        padding: '24px 32px',
        borderBottom: '1px solid #F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    iconWrapper: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        margin: 0,
        fontSize: '22px',
        fontWeight: 700,
        color: '#111827'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '10px',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    form: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
    },
    row: {
        display: 'flex',
        gap: '16px',
        width: '100%'
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    label: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#4B5563',
        marginInlineStart: '4px'
    },
    input: {
        padding: '14px 18px',
        borderRadius: '14px',
        border: '1.5px solid #E5E7EB',
        fontSize: '15px',
        outline: 'none',
        transition: 'all 0.2s',
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box'
    },
    inputReadOnly: {
        padding: '14px 18px',
        borderRadius: '14px',
        border: '1.5px solid #F3F4F6',
        backgroundColor: '#F9FAFB',
        fontSize: '15px',
        color: '#6B7280',
        cursor: 'not-allowed',
        outline: 'none',
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box'
    },
    textarea: {
        padding: '14px 18px',
        borderRadius: '14px',
        border: '1.5px solid #E5E7EB',
        fontSize: '15px',
        outline: 'none',
        transition: 'all 0.2s',
        fontFamily: 'inherit',
        resize: 'none',
        width: '100%',
        boxSizing: 'border-box'
    },
    fileInputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 18px',
        borderRadius: '14px',
        border: '1.5px dashed #D1D5DB',
        backgroundColor: '#F7F8F9',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    fileInput: {
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0,
        width: '100%',
        height: '100%',
        cursor: 'pointer'
    },
    fileLabel: {
        fontSize: '14px',
        color: '#6B7280',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '85%'
    },
    clearFileBtn: {
        position: 'absolute',
        right: '12px',
        background: '#E5E7EB',
        border: 'none',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 2
    },
    submitBtn: {
        height: '56px',
        borderRadius: '16px',
        border: 'none',
        color: 'white',
        fontSize: '16px',
        fontWeight: 700,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        marginTop: '8px',
        width: '100%'
    },
    spinner: {
        animation: 'spin 1s linear infinite'
    }
};

export default SupportModal;
